// ============================================================================
// useAtendimentoAcesso · quem vê o Simulador e os Pré-agendamentos (13/10/2026)
// ============================================================================
// O SERVIDOR decide (/api/atendimento-fila?op=me). Aqui só lemos a resposta
// para montar menu e telas:
//   { loading, erro, rollout, podeSimulador, podeFila, unidades, master, recarregar }
//
// · Master: responde na hora (acesso total, sem esperar) e confere o rollout
//   em segundo plano (para a tela saber se mostra "Avisar a recepção").
// · Demais: 1 chamada a cada 5 min por sessão (cache no sessionStorage por
//   conta + token; chamadas simultâneas, Sidebar e página, dividem a mesma).
// · 403 e 404 (rollout desligado) = sem acesso (guardado no cache).
// · FALHA ≠ sem acesso (REGRA LINEAR): rede ou servidor fora → `erro`, nada
//   em cache, e o hook tenta de novo sozinho (5 s, 10 s, 20 s… até 1 min, ao
//   voltar o foco da aba e ao voltar a rede).
// Uso: useAtendimentoAcesso({ enabled: isRecepcionista || isGerente })
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { sessionToken } from './secureRest.js';
import { filaApi, UNIDADES_ATD } from './atendimentoApi.js';

const CACHE_KEY = 'fk_atendimento_acesso';
const TTL_MS = 5 * 60 * 1000;
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;
const NEGADO = { rollout: null, podeSimulador: false, podeFila: false, unidades: [], master: false };
const emVoo = new Map();
const ouvintes = new Set();

function chaveSessao(user) {
  const tok = sessionToken();
  if (!user?.id || !tok) return '';
  return `${user.id}|${tok.slice(-16)}`;
}

function lerCache(chave) {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (c && c.k === chave && c.v && typeof c.v === 'object' && Date.now() - Number(c.em || 0) < TTL_MS) return { ...NEGADO, ...c.v };
  } catch { /* storage bloqueado: busca no servidor */ }
  return null;
}

function gravarCache(chave, v) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ k: chave, v, em: Date.now() })); } catch { /* best-effort */ }
}

function normalizar(j) {
  return {
    rollout: typeof j?.rollout === 'string' ? j.rollout : null,
    podeSimulador: j?.podeSimulador === true,
    podeFila: j?.podeFila === true,
    unidades: Array.isArray(j?.unidades) ? j.unidades.filter(u => UNIDADES_ATD.includes(u)) : [],
    master: j?.master === true,
  };
}

function buscar(chave) {
  let p = emVoo.get(chave);
  if (!p) {
    p = filaApi.me()
      .then(j => normalizar(j))
      .catch(err => {
        if (err?.status === 403 || err?.status === 404) return { ...NEGADO, rollout: err.status === 404 ? 'off' : null };
        throw err;
      })
      .then(v => {
        gravarCache(chave, v);
        for (const fn of ouvintes) { try { fn(chave, v); } catch { /* ouvinte desmontando */ } }
        return v;
      })
      .finally(() => { emVoo.delete(chave); });
    emVoo.set(chave, p);
  }
  return p;
}

const igual = (a, b) => a.loading === b.loading && a.erro === b.erro && a.rollout === b.rollout
  && a.podeSimulador === b.podeSimulador && a.podeFila === b.podeFila && a.master === b.master
  && JSON.stringify(a.unidades) === JSON.stringify(b.unidades);

const DO_MASTER = { podeSimulador: true, podeFila: true, unidades: [...UNIDADES_ATD], master: true };

export function useAtendimentoAcesso({ enabled = true } = {}) {
  const { user, isMaster } = useAuth();
  const chave = enabled ? chaveSessao(user) : '';

  const inicial = () => {
    if (!chave) return { loading: false, erro: '', ...NEGADO };
    const c = lerCache(chave);
    if (isMaster) return { loading: false, erro: '', ...NEGADO, rollout: c?.rollout || null, ...DO_MASTER };
    return c ? { loading: false, erro: '', ...c } : { loading: true, erro: '', ...NEGADO };
  };
  const [state, setState] = useState(inicial);
  const [tentativa, setTentativa] = useState(0);
  const [falhas, setFalhas] = useState(0);
  const recarregar = useCallback(() => setTentativa(t => t + 1), []);

  useEffect(() => {
    const aplicar = (next) => setState(prev => (igual(prev, next) ? prev : next));
    const doServidor = (v) => (isMaster ? { ...NEGADO, rollout: v.rollout, ...DO_MASTER } : { ...NEGADO, ...v });
    if (!chave) { aplicar({ loading: false, erro: '', ...NEGADO }); setFalhas(0); return undefined; }
    const c = lerCache(chave);
    if (c) { aplicar({ loading: false, erro: '', ...doServidor(c) }); setFalhas(0); return undefined; }
    let vivo = true;
    if (!isMaster) {
      setState(prev => {
        const next = { loading: true, ...NEGADO, erro: prev.erro || '' };
        return igual(prev, next) ? prev : next;
      });
    }
    buscar(chave)
      .then(v => { if (vivo) { aplicar({ loading: false, erro: '', ...doServidor(v) }); setFalhas(0); } })
      .catch(err => {
        if (!vivo) return;
        if (isMaster) { aplicar({ loading: false, erro: '', ...NEGADO, ...DO_MASTER }); return; } // master segue com acesso total
        aplicar({ loading: false, ...NEGADO, erro: err?.message || 'Não foi possível verificar o acesso agora.' });
        setFalhas(f => f + 1);
      });
    return () => { vivo = false; };
  }, [chave, tentativa, isMaster]);

  useEffect(() => {
    if (!chave) return undefined;
    const fn = (k, v) => {
      if (k !== chave) return;
      setState(prev => {
        const next = { loading: false, erro: '', ...NEGADO, ...(isMaster ? { rollout: v.rollout, ...DO_MASTER } : v) };
        return igual(prev, next) ? prev : next;
      });
      setFalhas(0);
    };
    ouvintes.add(fn);
    return () => { ouvintes.delete(fn); };
  }, [chave, isMaster]);

  useEffect(() => {
    if (!chave || falhas === 0) return undefined;
    const espera = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (falhas - 1));
    const t = setTimeout(recarregar, espera);
    const onVisivel = () => { if (document.visibilityState === 'visible') recarregar(); };
    document.addEventListener('visibilitychange', onVisivel);
    window.addEventListener('online', recarregar);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisivel);
      window.removeEventListener('online', recarregar);
    };
  }, [chave, falhas, recarregar]);

  return { ...state, recarregar };
}

export default useAtendimentoAcesso;
